"use client";

import React, { useRef, useCallback } from "react";
import styles from "./styles/Sidebar.module.css";

export default function Sidebar({
  teacher,
  user,
  data,
  onEditSidebar,
  onSeeAvailableTimes,
  availableTimesTargetId = "offeredSectionsAnchor",
}) {
  const canEdit =
    user?.role === "ADMIN" ||
    user?.role === "SUPER_ADMIN" ||
    (user?.role === "TEACHER" && teacher?.id === user?.id);

  const boxRef = useRef(null);

  const handleSeeTimes = useCallback(
    (e) => {
      if (e?.preventDefault) e.preventDefault();
      if (e?.stopPropagation) e.stopPropagation();

      if (
        typeof document !== "undefined" &&
        document.activeElement &&
        typeof document.activeElement.blur === "function"
      ) {
        document.activeElement.blur();
      }

      if (typeof window === "undefined") return;

      const el = document.getElementById(availableTimesTargetId);
      if (!el) {
        onSeeAvailableTimes?.();
        return;
      }

      const isPhone =
        (window.matchMedia?.("(max-width: 767px)")?.matches ?? false) ||
        window.innerWidth < 768;

      if (!isPhone) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        onSeeAvailableTimes?.();
        return;
      }

      const headerOffset = 180;

      window.setTimeout(() => {
        const rect = el.getBoundingClientRect();
        const targetY = window.scrollY + rect.top - headerOffset;

        window.scrollTo({
          top: Math.max(targetY, 0),
          behavior: "smooth",
        });

        window.setTimeout(() => {
          const rect2 = el.getBoundingClientRect();
          const correctedY = window.scrollY + rect2.top - headerOffset;
          if (Math.abs(correctedY - window.scrollY) > 120) {
            window.scrollTo({
              top: Math.max(correctedY, 0),
              behavior: "auto",
            });
          }
        }, 350);
      }, 120);

      onSeeAvailableTimes?.();
    },
    [availableTimesTargetId, onSeeAvailableTimes]
  );

  return (
    <div className={styles.enrollBox} ref={boxRef}>
      <div className={styles.topRow}>
        <div className={styles.priceCol}>
          <div className={styles.priceRow}>
            <p className={styles.priceHighlight}>
              <span className={styles.priceWrap}>
                <span className={styles.price}>${data.weeklyPrice}</span>
              </span>
              <span className={styles.priceRight}>
                /week{" "}
                <span className={styles.priceAlt}>
                  or ${data.totalPrice} overall
                </span>
              </span>
            </p>
            <button
              type="button"
              className={styles.tryFreeBox}
              onMouseDown={(ev) => ev.preventDefault()}
              onTouchStart={(ev) => ev.preventDefault()}
              onClick={handleSeeTimes}
              aria-label="Try for free"
            >
              <span className={styles.tryFreeBadge}>Try for free</span>
            </button>
          </div>
        </div>
        <div className={styles.enrollDetails}>
          <div className={styles.enrollItem}>
            <img src="/images/calendar-icon.svg" alt="Calendar Icon" />
            <span>
              {data.weeklyFrequency}x/week, {data.courseDuration} weeks
            </span>
          </div>
          <div className={styles.enrollItem}>
            <img src="/images/clock-icon.svg" alt="Clock Icon" />
            <span>{data.singleClassDuration}</span>
          </div>

          {data.completionCount > 0 && (
            <div className={styles.enrollItem}>
              <img src="/images/group-icon.svg" alt="Group Icon" />
              <span>{data.completionCount} learners completed</span>
            </div>
          )}

          <div className={styles.enrollItem}>
            <img src="/images/age-icon.svg" alt="Age Icon" />
            <span>Age: {data.ages}</span>
          </div>

          <div className={styles.enrollItem}>
            <img src="/images/group-icon.svg" alt="Group Icon" />
            <span>{data.numLearners} learners</span>
          </div>

          <button
            type="button"
            className={styles.ctaWrap}
            onMouseDown={(ev) => ev.preventDefault()}
            onTouchStart={(ev) => ev.preventDefault()}
            onClick={handleSeeTimes}
          >
            See All Available Times
          </button>
        </div>
      </div>

      {canEdit ? (
        <div className={styles.bottomRow}>
          <button
            type="button"
            className={`${styles.btn} ${styles.editBtn}`}
            onClick={onEditSidebar}
          >
            Edit Sidebar
          </button>
        </div>
      ) : null}
    </div>
  );
}